import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import Icon from '../Icon'

import styles from './style.module.scss'

/*
 * Ein Filterfeld der Leiste — fuer alle Ansichten dasselbe.
 *
 * Bewusst ein Aufklappfeld und kein aufklappender Block: Das Menue liegt
 * *ueber* der Leiste, es schiebt also die Felder darunter nicht weg. Bei
 * einem Dutzend Feldern ist das der Unterschied zwischen "Schule waehlen" und
 * "erst einmal ans Ende der Leiste scrollen".
 *
 * Zwei Quellen fuer die Auswahl:
 * - `options`: feste Liste, im Browser durchsuchbar (Bundesland, Typ, ...)
 * - `loadOptions`: kommt vom Server, mit Suche und Nachladen beim Scrollen
 *   (Schule, Lehrkraft — mehrere hundert Eintraege, die niemand am Stueck holt)
 */

const PAGE_SIZE = 25
const SEARCH_DELAY = 300

export default function FilterField({ field, values, onChange }) {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState('')
	const [loadedOptions, setLoadedOptions] = useState([])
	const [hasMore, setHasMore] = useState(false)
	const [loading, setLoading] = useState(false)
	const rootRef = useRef(null)
	const inputRef = useRef(null)
	const requestRef = useRef(0)

	const isAsync = typeof field.loadOptions === 'function'

	const load = useCallback(
		async (term, offset) => {
			const request = ++requestRef.current
			setLoading(true)
			try {
				const result = await field.loadOptions({ search: term, offset, limit: PAGE_SIZE })
				// Eine langsamere aeltere Anfrage darf ein neueres Ergebnis nicht ueberschreiben.
				if (request !== requestRef.current) return
				setLoadedOptions(previous => (offset ? [...previous, ...result.options] : result.options))
				setHasMore(result.hasMore)
			} finally {
				if (request === requestRef.current) setLoading(false)
			}
		},
		[field]
	)

	// Klick daneben und Esc schliessen das Menue.
	useEffect(() => {
		if (!open) return undefined

		const onPointerDown = event => {
			if (!rootRef.current?.contains(event.target)) setOpen(false)
		}
		const onKeyDown = event => {
			if (event.key === 'Escape') {
				event.stopPropagation()
				setOpen(false)
			}
		}

		document.addEventListener('mousedown', onPointerDown)
		document.addEventListener('keydown', onKeyDown)
		return () => {
			document.removeEventListener('mousedown', onPointerDown)
			document.removeEventListener('keydown', onKeyDown)
		}
	}, [open])

	useEffect(() => {
		if (open) inputRef.current?.focus()
	}, [open])

	// Nach oben aufklappen, wenn darunter kein Platz ist (Anforderung #185): Das
	// Menue haengt in der rollenden Flaeche der Leiste und wurde beim untersten
	// Feld an deren Unterkante abgeschnitten — die Liste war nur halb zu sehen.
	const [dropUp, setDropUp] = useState(false)
	useLayoutEffect(() => {
		if (!open) {
			setDropUp(false)
			return
		}
		setDropUp(menuOpensUp(rootRef.current))
	}, [open])

	// Serverseitige Suche: erst tippen lassen, dann fragen.
	useEffect(() => {
		if (!open || !isAsync) return undefined

		const timer = setTimeout(() => load(search, 0), search ? SEARCH_DELAY : 0)
		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, search, isAsync])

	const options = isAsync
		? loadedOptions
		: (field.options || []).filter(option =>
				String(option.label).toLowerCase().includes(search.trim().toLowerCase())
		  )

	const isChecked = value => values.some(option => String(option.value) === String(value))

	const toggle = option => {
		if (isChecked(option.value)) onChange(values.filter(item => String(item.value) !== String(option.value)))
		else onChange([...values, option])
	}

	const onScroll = event => {
		if (!isAsync || loading || !hasMore) return
		const { scrollTop, scrollHeight, clientHeight } = event.target
		if (scrollTop + clientHeight >= scrollHeight - 24) load(search, loadedOptions.length)
	}

	const summary = () => {
		if (!values.length) return t('All')
		if (values.length === 1) return values[0].label
		return `${values.length} ${t('selected')}`
	}

	return (
		// `role="group"` mit Beschriftung: Beschriftung, Aufklappknopf, Merkzettel
		// und Menue gehoeren zusammen und sind sonst nur ueber ihre Reihenfolge im
		// Baum als ein Feld erkennbar — fuer Vorlesesoftware wie fuer Tests.
		<div className={styles.field} role="group" aria-label={field.label} ref={rootRef}>
			<div className={styles.fieldHead}>
				<span className={styles.fieldLabel}>{field.label}</span>
				{!!values.length && (
					<button
						type="button"
						className={styles.fieldClear}
						onClick={() => onChange([])}
						title={t('Reset')}
					>
						{t('Reset')}
					</button>
				)}
			</div>

			<button
				type="button"
				className={clsx(styles.control, values.length && styles.controlActive, open && styles.controlOpen)}
				onClick={() => setOpen(!open)}
				aria-expanded={open}
			>
				<span className={styles.controlText}>{summary()}</span>
				<Icon className={clsx(styles.caret, open && styles.caretOpen)} name="arrow" size={{ w: 10, h: 10 }} />
			</button>

			{open && (
				<div className={clsx(styles.menu, dropUp && styles.menuUp)}>
					<div className={styles.menuSearch}>
						<input
							ref={inputRef}
							className={styles.menuInput}
							value={search}
							placeholder={t('Search')}
							onChange={event => setSearch(event.target.value)}
						/>
						{!!search && (
							<button
								type="button"
								className={styles.menuInputClear}
								onClick={() => setSearch('')}
								title={t('Reset')}
							>
								<Icon name="close" size={{ w: 8, h: 8 }} noFill={true} />
							</button>
						)}
					</div>

					<div className={styles.menuList} onScroll={onScroll}>
						{options.map(option => (
							<label className={styles.menuOption} key={String(option.value)}>
								<input
									type="checkbox"
									className={styles.menuCheckbox}
									checked={isChecked(option.value)}
									onChange={() => toggle(option)}
								/>
								<span className={styles.menuOptionLabel}>{option.label}</span>
							</label>
						))}

						{!options.length && !loading && <p className={styles.menuEmpty}>{t('No entries')}</p>}
						{loading && <p className={styles.menuEmpty}>{t('Loading')}…</p>}
					</div>
				</div>
			)}

			{!!values.length && (
				<div className={styles.chips}>
					{values.map(option => (
						<span className={styles.chip} key={String(option.value)}>
							<span className={styles.chipLabel}>{option.label}</span>
							<button
								type="button"
								className={styles.chipRemove}
								onClick={() => toggle(option)}
								title={t('Remove')}
							>
								<Icon name="close" size={{ w: 8, h: 8 }} noFill={true} />
							</button>
						</span>
					))}
				</div>
			)}
		</div>
	)
}

FilterField.propTypes = {
	field: PropTypes.shape({
		key: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		options: PropTypes.array,
		loadOptions: PropTypes.func
	}).isRequired,
	values: PropTypes.array.isRequired,
	onChange: PropTypes.func.isRequired
}

/*
 * Platz unter bzw. ueber dem Feld, gemessen an der naechsten rollenden Flaeche
 * (der Leiste), nicht am Fenster: Abgeschnitten wird an der Leiste.
 */
const MENU_HEIGHT = 300

export function menuOpensUp(element) {
	if (!element) return false
	let frame = element.parentElement
	while (frame && frame !== document.body) {
		const { overflowY } = window.getComputedStyle(frame)
		if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') break
		frame = frame.parentElement
	}
	const bounds = (frame && frame !== document.body ? frame : document.documentElement).getBoundingClientRect()
	const field = element.getBoundingClientRect()
	const below = Math.min(bounds.bottom, window.innerHeight) - field.bottom
	const above = field.top - Math.max(bounds.top, 0)
	return below < MENU_HEIGHT && above > below
}
